import { useEffect, useState } from 'react'
import { api, type Principal } from '../api'
import { BrandLogo, BrandShell, ProductName, Wordmark } from '../components/Brand'

/**
 * §10.1. A password here, or the firm's own identity provider. GUARD asks for no
 * second factor of its own (IMPLEMENTATION.md decision 88): that is the provider's.
 *
 * The failure message is whatever the API returned. An unknown address and a
 * wrong password fail identically by design, so the UI must not try to be more
 * helpful than the server was: doing so would rebuild the account-enumeration
 * oracle `identity.py` goes out of its way to close.
 *
 * **Sign in with Microsoft appears only when this appliance federates.** §2's
 * appliance may sit on a network with no route to `login.microsoftonline.com` at
 * all, and a button that always fails is how users learn to ignore errors. It is
 * a link rather than a fetch: the flow is a full-page journey to the identity
 * provider and back, which is not something `fetch` can perform.
 */
export function SignIn({
  onSignedIn,
  onRedeem,
}: {
  onSignedIn: (p: Principal) => void
  onRedeem: () => void
}) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [entra, setEntra] = useState(false)

  useEffect(() => {
    api
      .providers()
      .then((p) => setEntra(p.entra))
      .catch(() => setEntra(false))
  }, [])

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      onSignedIn(await api.signIn(email, password))
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <BrandShell>
      <form className="signin" onSubmit={submit}>
        {/* The same lockup as the signed-in header, so the way in and the
            application read as one product: what this is, then who makes it. */}
        <h1 className="product-title">
          <Wordmark size="title" />
        </h1>
        <p className="by-line">
          <span className="by">by</span>
          <BrandLogo className="by-logo" />
        </p>
        <p className="tagline">One source of truth for investment guidelines.</p>
      <label>
        Email
        <input
          type="email"
          value={email}
          autoComplete="username"
          onChange={(e) => setEmail(e.target.value)}
          required
        />
      </label>
      <label>
        Password
        <input
          type="password"
          value={password}
          autoComplete="current-password"
          onChange={(e) => setPassword(e.target.value)}
          required
        />
      </label>
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      <button type="submit" disabled={busy}>
        {busy ? 'Signing in…' : 'Sign in'}
      </button>
      <p className="federated">
        <a
          href="#redeem"
          onClick={(e) => {
            e.preventDefault()
            onRedeem()
          }}
        >
          I have an invitation
        </a>
        <small>
          New accounts start with a single-use invitation from your
          administrator. Redeem it here to choose a password.
        </small>
      </p>
      {entra && (
        <p className="federated">
          <a href="/api/auth/entra/start">Sign in with Microsoft</a>
          <small>
            Your <ProductName /> account has to exist already. Microsoft confirms who
            you are; your administrator sets what you can do.
          </small>
        </p>
      )}
      </form>
    </BrandShell>
  )
}
